import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Minus, Plus, Scan, X } from 'lucide-react';
import { VisuallyHidden } from 'radix-ui';
import { cn } from '../../lib/utils';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '../ui/dialog';
import { MermaidSvg } from './MermaidSvg';
import { useWheelZoom } from '../../hooks/useWheelZoom';
import {
  DEFAULT_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  focalScroll,
  formatZoom,
  zoomBy,
  zoomByWheelSteps,
} from './mermaid-zoom';

/**
 * A zoom level together with the point it should hold still, in viewport
 * coordinates. The two travel as one value because every zoom has an anchor:
 * the wheel's is the cursor, everything else's is the middle of the view. A
 * separate ref for the anchor would go stale the moment a gesture was clamped
 * at a bound and produced no zoom change to consume it.
 */
interface ZoomTarget {
  zoom: number;
  anchor: { x: number; y: number } | null;
}

const FIT: ZoomTarget = { zoom: DEFAULT_ZOOM, anchor: null };

export interface MermaidZoomDialogProps {
  /** The compiled diagram, from `useMermaidSvg`. */
  svg: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The expanded view of a Mermaid diagram: the same SVG on a near-full-screen
 * surface, with zoom controls for the cases the inline block cannot serve.
 *
 * Inline, mermaid caps the diagram at its natural width, so a graph with a
 * dozen nodes renders its labels at a few pixels tall inside a chat column.
 * Here the diagram is scaled to fit the dialog instead, which is already a
 * large jump, and zooming past that grows the layout box so the surrounding
 * scroll container gives real scrollbars — a CSS `transform` would scale the
 * pixels without extending anything to scroll.
 */
export function MermaidZoomDialog({ svg, open, onOpenChange }: MermaidZoomDialogProps) {
  const [target, setTarget] = useState<ZoomTarget>(FIT);
  const zoom = target.zoom;

  // Reopening starts fit-to-view. Carrying the previous zoom over would drop
  // the next diagram in at 400%, scrolled to a corner of something else.
  useEffect(() => {
    if (open) setTarget(FIT);
  }, [open]);

  // Buttons and keys have no cursor to zoom toward, so they leave the anchor
  // unset and the viewport falls back to the center of the view.
  const step = (direction: 1 | -1) =>
    setTarget((t) => ({ zoom: zoomBy(t.zoom, direction), anchor: null }));
  // Fit needs no scroll reset: at 100% the diagram is exactly the size of its
  // viewport, so there is nothing to scroll and the browser pins it to 0.
  const fit = () => setTarget(FIT);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[92vh] w-[95vw] max-w-[95vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[95vw]"
        showCloseButton={false}
        aria-describedby={undefined}
        data-testid="mermaid-zoom-dialog"
        onKeyDown={(e) => {
          if (e.metaKey || e.ctrlKey || e.altKey) return;
          if (e.key === '+' || e.key === '=') {
            e.preventDefault();
            step(1);
          } else if (e.key === '-') {
            e.preventDefault();
            step(-1);
          } else if (e.key === '0') {
            e.preventDefault();
            fit();
          }
        }}
      >
        <VisuallyHidden.Root>
          <DialogTitle>Diagram</DialogTitle>
        </VisuallyHidden.Root>
        <div className="flex items-center gap-1 border-b border-border/60 bg-muted/40 px-3 py-2">
          <span className="flex-1 truncate font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
            mermaid
          </span>
          <ZoomButton
            label="Zoom out"
            icon={Minus}
            onClick={() => step(-1)}
            disabled={zoom <= MIN_ZOOM}
          />
          <span
            className="w-12 text-center font-mono text-[11px] text-muted-foreground tabular-nums"
            data-testid="mermaid-zoom-level"
            aria-live="polite"
          >
            {formatZoom(zoom)}
          </span>
          <ZoomButton
            label="Zoom in"
            icon={Plus}
            onClick={() => step(1)}
            disabled={zoom >= MAX_ZOOM}
          />
          <ZoomButton label="Fit to view" icon={Scan} onClick={fit} className="ml-1" />
          <DialogClose
            aria-label="Close diagram"
            className="ml-1 rounded p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <X className="h-3.5 w-3.5" />
          </DialogClose>
        </div>
        <ZoomViewport target={target} svg={svg} setTarget={setTarget} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * The scrolling surface the diagram is laid out on. Beyond fit-to-view the
 * diagram is larger than the dialog, so it pans by dragging as well as by
 * scrolling — reaching for a scrollbar to read the far end of a flowchart is
 * not what "expand this" was asking for.
 *
 * The wheel zooms rather than scrolls, matching the image viewer: over a
 * picture that is bigger than its frame, a scroll gesture reads as "get me
 * closer", and the drag-to-pan above is what moves around at that zoom.
 */
function ZoomViewport({
  target,
  svg,
  setTarget,
}: {
  target: ZoomTarget;
  svg: string | null;
  setTarget: (update: (target: ZoomTarget) => ZoomTarget) => void;
}) {
  const { zoom, anchor } = target;
  const viewport = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const [dragging, setDragging] = useState(false);

  // Growing the layout box alone leaves the scroll offsets where they were,
  // which pins the top-left corner and sends whatever the reader was looking
  // at off the bottom-right.
  const previousZoom = useRef(zoom);
  useLayoutEffect(() => {
    const el = viewport.current;
    const previous = previousZoom.current;
    previousZoom.current = zoom;
    if (!el || previous === zoom) return;
    const ratio = zoom / previous;
    el.scrollLeft = focalScroll(el.scrollLeft, anchor?.x ?? el.clientWidth / 2, ratio);
    el.scrollTop = focalScroll(el.scrollTop, anchor?.y ?? el.clientHeight / 2, ratio);
    // `anchor` arrives in the same update as the zoom it belongs to, and a
    // rerun where only the anchor moved is caught by the guard above.
  }, [zoom, anchor]);

  // Anchored on the cursor: the scroll offsets are this viewer's stand-in for
  // a transform, so the focal point is measured from the viewport's top-left.
  useWheelZoom(viewport, (steps, event) => {
    const rect = viewport.current?.getBoundingClientRect();
    if (!rect) return;
    setTarget((t) => ({
      zoom: zoomByWheelSteps(t.zoom, steps),
      anchor: { x: event.clientX - rect.left, y: event.clientY - rect.top },
    }));
  });

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div
      ref={viewport}
      className={cn(
        'flex-1 overflow-auto bg-card',
        dragging ? 'cursor-grabbing select-none' : 'cursor-grab',
      )}
      data-testid="mermaid-zoom-viewport"
      onPointerDown={(e) => {
        // Primary button only: middle-click is paste-on-Linux and right-click
        // is the context menu, and neither should start a pan.
        if (e.button !== 0) return;
        const el = e.currentTarget;
        drag.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
        setDragging(true);
        el.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const start = drag.current;
        if (!start) return;
        e.currentTarget.scrollLeft = start.left - (e.clientX - start.x);
        e.currentTarget.scrollTop = start.top - (e.clientY - start.y);
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {/* The diagram's layout box. Percentages resolve against the viewport,
          so 100% is fit-to-view and anything above it overflows into scroll.
          The padding lives here rather than on the viewport so the box is an
          exact multiple of the viewport, which is what the centering math
          above assumes. `!` on the sizing classes because mermaid writes an
          inline `max-width` onto the SVG that a plain class would lose to. */}
      <div
        className="p-4 [&_svg]:h-full! [&_svg]:w-full! [&_svg]:max-w-none!"
        style={{ width: `${zoom * 100}%`, height: `${zoom * 100}%` }}
      >
        <MermaidSvg svg={svg} className="h-full w-full" />
      </div>
    </div>
  );
}

function ZoomButton({
  label,
  icon: Icon,
  onClick,
  disabled,
  className,
}: {
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'rounded p-1 text-muted-foreground transition-colors',
        'hover:bg-muted hover:text-foreground',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-40',
        className,
      )}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
